import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import Constants from "../../helpers/Constants";
import PictureImg from "../../helpers/PictureImg";

// ---------------------------------------------------------------------------
// Hero Section — Figma node 294:34502 (hero, dark theme)
//
// Layout (1920 design):
//   hero (1920 x 1124, #0B0D0F)
//   ├─ contents @ y=154, h=654
//   │   ├─ hero-text (708 wide) @ x=240
//   │   │   ├─ title-block (h=176)
//   │   │   │   ├─ marque-hero-title (rotating word, h=88)
//   │   │   │   └─ "그로잉세일즈" + "로 연결하세요" (h=88)
//   │   │   ├─ desc (630, 2 lines via \n)
//   │   │   ├─ buttons (2 CTAs)
//   │   │   └─ stats
//   │   └─ hero-image (852 wide) @ x=1068 — bleeds to right edge
//   └─ marquee @ y=908, h=168 (brand logos + caption)
//
// Animations:
//   - marque-hero-title: AFTER_TIMEOUT 4s, ease-out cubic 0.6s, y-slide
//   - hero-image: 4 separate cards (Figma 490:8383) appear progressively:
//       hero-1: main 장바구니 dashboard (centered + 147 right offset)
//       hero-2: order-list (left/bottom)
//       hero-3: point card (right/top)
//       hero-4: level-status (right/middle)
//     Each step adds the next card on top via the `is-on` class. The
//     sequence runs ONCE (1 → 1+2 → 1+2+3 → 1+2+3+4) and then stops at
//     the final state with all 4 cards visible. (2026-05-06 디자이너 요청)
//   - marquee: 40s linear infinite translateX(0 → -50%)
// ---------------------------------------------------------------------------

const ROTATING_WORDS = [
  "매장과 온라인 자사몰을",
  "매장 포스를",
  "온오프라인 통합 멤버십은",
  "판매부터 CRM까지",
];

// 25 brand logos exported from Figma COMPONENT_SET `brand-logo` (249:22191).
// SVGs live in public/img/landing/logo-<slug>.svg and already ship with a
// neutral gray fill (#838B96) baked in so they read well on the dark hero.
const BRAND_LOGOS = [
  { src: "/img/landing/logo-tirtir.svg", alt: "티르티르" },
  { src: "/img/landing/logo-arenakorea.svg", alt: "아레나코리아" },
  { src: "/img/landing/logo-bluedog.svg", alt: "블루독" },
  { src: "/img/landing/logo-standoil.svg", alt: "스탠드오일" },
  { src: "/img/landing/logo-toniwek.svg", alt: "토니웩" },
  { src: "/img/landing/logo-sevy.svg", alt: "세비" },
  { src: "/img/landing/logo-heights.svg", alt: "헤이츠" },
  { src: "/img/landing/logo-kindersalmon.svg", alt: "킨더살몬" },
  { src: "/img/landing/logo-granhand.svg", alt: "그랑핸드" },
  { src: "/img/landing/logo-ihatemonday.svg", alt: "아이헤이트먼데이" },
  { src: "/img/landing/logo-urbanside.svg", alt: "어반사이드" },
  { src: "/img/landing/logo-facadepattern.svg", alt: "파사드패턴" },
  { src: "/img/landing/logo-puppies.svg", alt: "퍼피즈" },
  { src: "/img/landing/logo-sunburnproject.svg", alt: "썬번프로젝트" },
  { src: "/img/landing/logo-etcseoul.svg", alt: "이티씨서울" },
  { src: "/img/landing/logo-donaandy.svg", alt: "도나앤디" },
  { src: "/img/landing/logo-artifacts.svg", alt: "아트이프액츠" },
  { src: "/img/landing/logo-nominate.svg", alt: "노미네이트" },
  { src: "/img/landing/logo-walong.svg", alt: "우알롱" },
  { src: "/img/landing/logo-bellier.svg", alt: "벨리에" },
  { src: "/img/landing/logo-youth.svg", alt: "유스" },
  { src: "/img/landing/logo-inae.svg", alt: "인에이" },
  { src: "/img/landing/logo-habati.svg", alt: "하바티" },
  { src: "/img/landing/logo-notfornerd.svg", alt: "낫포너드" },
];

export default function Hero() {
  const [wordIdx, setWordIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setWordIdx((i) => (i + 1) % ROTATING_WORDS.length);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // 우진 (2026-05-06): 4단계까지 한 번 다 노출되면 멈춘다. 디자이너 요청
    // 으로 loop 제거 — 모든 카드가 켜진 최종 상태에서 정적으로 유지.
    const t = setInterval(() => {
      setImgIdx((i) => {
        if (i >= 3) {
          clearInterval(t);
          return i;
        }
        return i + 1;
      });
    }, 900);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="hero">
      <div className="hero__blob hero__blob--tl" aria-hidden="true" />
      <div className="hero__blob hero__blob--bl" aria-hidden="true" />

      <div className="hero__contents">
        <div className="hero__text">
          <div className="hero__title-block">
            {/* Line 1: rotating word
                우진 (2026-05-08): 글자 수 ≥ 11 인 긴 문구만 --long modifier
                부여 → 모바일에서 좁은 viewport 일 때 font-size clamp 으로
                자동 축소. 짧은 문구는 Figma spec 그대로 36px 유지. 가운데
                정렬은 SCSS (모바일 only) 에서 처리. */}
            <div className="hero__title-rotator" aria-live="polite">
              <AnimatePresence mode="wait">
                <motion.span
                  key={wordIdx}
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -60, opacity: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className={
                    "hero__rotator-word" +
                    (ROTATING_WORDS[wordIdx].length >= 11
                      ? " hero__rotator-word--long"
                      : "")
                  }
                >
                  {ROTATING_WORDS[wordIdx]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* Line 2: "그로잉세일즈" (gradient) + "로 연결하세요" (white) */}
            <h1 className="hero__title">
              <span className="hero__title-accent">그로잉세일즈</span>
              <span className="hero__title-plain">&nbsp;로</span>
            </h1>
          </div>

          <p className="hero__desc">
            {
              "매장과 자사몰이 실시간으로 연결됩니다.\n적립금·쿠폰·회원 정보는 양방향으로 흐르고, 매출과 재고는 하나의 대시보드에 모입니다."
            }
          </p>

          <div className="hero__buttons">
            {/* 우진 (2026-04-30): 무료로 시작하기 → 대시보드, 도입 상담 →
                채널톡. 둘 다 외부 도메인이라 신규 탭으로 연다. */}
            <a
              href={Constants.DASHBOARD_HOME}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--lg btn--primary"
            >
              무료로 시작하기
            </a>
            <a
              href={Constants.CHANNEL_TALK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--lg btn--secondary"
            >
              도입 상담 신청하기
            </a>
          </div>

          <div className="hero__stats">
            <span>사용후기 평점 4.6</span>
            <span className="hero__stats-divider" />
            <span>통합 관리 회원 220만 +</span>
          </div>
        </div>

        <div className="hero__image-wrap">
          {/* Per Figma 490:8383 — 4 absolute-positioned cards. Each cycles
              on with `is-on` based on imgIdx; hero-1 is the base, then 2, 3,
              4 layer on top. CSS handles each card's exact coordinates via
              the `--N` modifier. */}
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`hero__image hero__image--${n} ${
                n - 1 <= imgIdx ? "is-on" : ""
              }`}
            >
              {/* (2026-05-07) hero-1 은 LCP candidate — fetchpriority="high"
                  + eager 로 즉시 받음. 나머지 (2~4) 는 rotation 후 등장하므로
                  lazy 로 첫 로드 대역폭 침해 안 함. */}
              <PictureImg
                src={`/img/landing/hero-${n}.png`}
                alt=""
                loading={n === 1 ? "eager" : "lazy"}
                fetchpriority={n === 1 ? "high" : "auto"}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="hero__marquee">
        <div className="hero__marquee-track">
          {/* Duplicated so the -50% translate wraps seamlessly */}
          {[...BRAND_LOGOS, ...BRAND_LOGOS].map((logo, i) => (
            <span className="hero__marquee-item" key={i}>
              <PictureImg src={logo.src} alt={logo.alt} loading="lazy" />
            </span>
          ))}
        </div>
        <p className="hero__marquee-caption">
          도입 브랜드의 그로잉세일즈 <b>사용 유지율 96%</b>
        </p>
      </div>
    </section>
  );
}
